import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3,
  Trophy,
  Filter,
  Search,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Users,
  Star,
  Award,
  Target,
  Activity,
  Crown,
  Minus,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { standingsApi } from '../../services/api';
import { useSite } from '../../contexts/SiteContext';
import { useSeason } from '../../contexts/SeasonContext';
import SeasonSelector from '../Common/SeasonSelector';

// Types for standings
interface Standing {
  id: string;
  team: {
    name: string;
    city: string;
    abbreviation: string;
    sport_name: string;
  };
  rank: number;
  wins: number;
  losses: number;
  ties?: number;
  points?: number;
  win_percentage: number;
  games_played: number;
  games_behind?: number;
  streak?: string;
  streak_type?: 'win' | 'loss' | 'tie';
  division: string;
  conference?: string;
  league_name: string;
  season_name: string;
  last_updated: string;
  playoff_position?: 'champion' | 'playoffs' | 'bubble' | 'eliminated';
  home_record?: string;
  away_record?: string;
  division_record?: string;
  conference_record?: string;
}

const StandingsPage: React.FC = () => {
  const site = useSite();
  const { currentSeason } = useSeason();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [selectedConference, setSelectedConference] = useState('all');
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);

  // Fetch standings from API
  const { data: standingsData, isLoading, error } = useQuery({
    queryKey: ['standings', site.sport, selectedDivision, currentSeason],
    queryFn: async () => {
      try {
        let response;
        if (selectedDivision && selectedDivision !== 'all') {
          response = await standingsApi.getByDivision(selectedDivision, currentSeason);
        } else {
          response = await standingsApi.getBySeason(currentSeason);
        }
        return response.data || [];
      } catch (error) {
        console.error('Error fetching standings:', error);
        return [];
      }
    },
  });

  // Transform API data to match component interface
  const standings = {
    data: standingsData?.map((standing: any, index: number) => ({
      id: standing.id || Math.random().toString(),
      team: {
        name: standing.team?.name || 'Team',
        city: standing.team?.city || '',
        abbreviation: standing.team?.abbreviation || 'TM',
        sport_name: standing.team?.sport_name || site.sport || 'Sports'
      },
      rank: standing.rank || standing.position || index + 1,
      wins: standing.wins || 0,
      losses: standing.losses || 0,
      ties: standing.ties,
      points: standing.points,
      win_percentage: standing.win_percentage || (standing.wins && standing.losses ? standing.wins / (standing.wins + standing.losses) : 0),
      games_played: standing.games_played || ((standing.wins || 0) + (standing.losses || 0) + (standing.ties || 0)),
      games_behind: standing.games_behind,
      streak: standing.streak,
      streak_type: standing.streak_type,
      division: standing.division?.name || standing.division || 'Division',
      conference: standing.conference?.name || standing.conference,
      league_name: standing.league?.name || `${site.sport} League`,
      season_name: standing.season?.name || `${currentSeason}-${(currentSeason + 1).toString().slice(2)} Season`,
      last_updated: standing.last_updated || new Date().toISOString().split('T')[0],
      playoff_position: standing.playoff_position,
      home_record: standing.home_record,
      away_record: standing.away_record,
      division_record: standing.division_record,
      conference_record: standing.conference_record
    })) || []
  };


  const filteredStandings = standings?.data?.filter((standing: Standing) => {
    const matchesSearch = 
      standing.team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      standing.team.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      standing.team.abbreviation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      standing.division.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' || standing.team.sport_name.toLowerCase() === selectedSport;
    const matchesDivision = selectedDivision === 'all' || standing.division === selectedDivision;
    const matchesConference = selectedConference === 'all' || standing.conference === selectedConference;
    return matchesSearch && matchesSport && matchesDivision && matchesConference;
  }) || [];

  const sports = Array.from(new Set(standings?.data?.map(s => s.team.sport_name) || []));
  const divisions = Array.from(new Set(standings?.data?.map(s => s.division) || []));
  const conferences = Array.from(new Set(standings?.data?.map(s => s.conference).filter(Boolean) || []));

  const getPlayoffPositionColor = (position?: string) => {
    switch (position) {
      case 'champion': return 'text-primary';
      case 'playoffs': return 'text-success';
      case 'bubble': return 'text-warning';
      case 'eliminated': return 'text-error';
      default: return 'text-gray-400';
    }
  };

  const getPlayoffPositionBadge = (position?: string) => {
    switch (position) {
      case 'champion': return 'badge-primary';
      case 'playoffs': return 'badge-success';
      case 'bubble': return 'badge-warning';
      case 'eliminated': return 'badge-error';
      default: return 'badge-secondary';
    }
  };

  const getPlayoffPositionIcon = (position?: string) => {
    switch (position) {
      case 'champion': return Crown;
      case 'playoffs': return Trophy;
      case 'bubble': return Target;
      case 'eliminated': return Minus;
      default: return BarChart3;
    }
  };

  const getStreakIcon = (streakType?: string) => {
    switch (streakType) {
      case 'win': return TrendingUp;
      case 'loss': return TrendingDown;
      default: return Minus;
    }
  };

  const getStreakColor = (streakType?: string) => {
    switch (streakType) {
      case 'win': return 'text-success';
      case 'loss': return 'text-error';
      default: return 'text-gray-400';
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return Crown;
    if (rank <= 3) return Trophy;
    if (rank <= 8) return Star;
    return Target;
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-primary';
    if (rank <= 3) return 'text-success';
    if (rank <= 8) return 'text-secondary';
    return 'text-gray-400';
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Sort standings by rank
  const sortedStandings = [...filteredStandings].sort((a, b) => a.rank - b.rank);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <BarChart3 className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                League Standings
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">TEAM </span>
              <span className="neon-text">STANDINGS</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Current standings and rankings across all major professional sports leagues
            </p>
          </div>

          {/* Season Selector */}
          <div className="flex justify-center mb-6">
            <SeasonSelector />
          </div>

          {/* Filters */}
          <div className="glass-card p-6 mb-8 overflow-hidden">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search teams or divisions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                <div className="relative flex-1 sm:flex-initial">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Sports</option>
                    {sports.map(sport => (
                      <option key={sport} value={sport?.toLowerCase()}>
                        {sport}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-initial">
                  <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedDivision}
                    onChange={(e) => setSelectedDivision(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Divisions</option>
                    {divisions.map(division => (
                      <option key={division} value={division}>
                        {division}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-initial">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedConference}
                    onChange={(e) => setSelectedConference(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Conferences</option>
                    {conferences.map(conference => (
                      <option key={conference} value={conference}>
                        {conference}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Crown className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sortedStandings.filter(s => s.playoff_position === 'champion').length}
              </div>
              <div className="text-sm text-gray-400">Champions</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-success mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sortedStandings.filter(s => s.playoff_position === 'playoffs').length}
              </div>
              <div className="text-sm text-gray-400">Playoff Teams</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Target className="w-8 h-8 text-warning mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sortedStandings.filter(s => s.playoff_position === 'bubble').length}
              </div>
              <div className="text-sm text-gray-400">On the Bubble</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 text-center"
            >
              <Users className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sortedStandings.length}
              </div>
              <div className="text-sm text-gray-400">Total Teams</div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Standings List */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : error ? (
          <div className="glass-card p-12 text-center">
            <BarChart3 className="w-16 h-16 text-error mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-white mb-2">Error Loading Standings</h3>
            <p className="text-gray-400">{(error as Error).message || 'An error occurred'}</p>
          </div>
        ) : sortedStandings.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Standings Found</h3>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-4"
          >
            {sortedStandings.map((standing) => {
              const RankIcon = getRankIcon(standing.rank);
              const StreakIcon = getStreakIcon(standing.streak_type);
              const PlayoffIcon = getPlayoffPositionIcon(standing.playoff_position);
              
              return (
                <motion.div
                  key={standing.id}
                  variants={item}
                  whileHover={{ scale: 1.01 }}
                  className="group"
                >
                  <div 
                    className="glass-card overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group-hover:shadow-[0_0_30px_rgba(0,255,136,0.15)]"
                    onClick={() => setExpandedTeam(
                      expandedTeam === standing.id ? null : standing.id
                    )}
                  >
                    {/* Main Standing Row */}
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        {/* Rank and Team */}
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getRankColor(standing.rank)} bg-current bg-opacity-20`}>
                              <span className="text-lg font-black">
                                {standing.rank}
                              </span>
                            </div>
                            <RankIcon className={`w-5 h-5 ${getRankColor(standing.rank)}`} />
                          </div>

                          {/* Team Info */}
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                              <span className="text-sm font-black text-primary">
                                {standing.team.abbreviation}
                              </span>
                            </div>
                            <div>
                              <div className="font-bold text-white group-hover:text-primary transition-colors">
                                {standing.team.name}
                              </div>
                              <div className="text-sm text-gray-400">
                                {standing.division}
                                {standing.conference && ` • ${standing.conference}`}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <span className="badge badge-primary text-xs">
                              {standing.team.sport_name}
                            </span>
                            {standing.playoff_position && (
                              <span className={`badge ${getPlayoffPositionBadge(standing.playoff_position)} text-xs`}>
                                {standing.playoff_position === 'champion' ? 'Champion' : 
                                 standing.playoff_position === 'playoffs' ? 'Playoffs' :
                                 standing.playoff_position === 'bubble' ? 'Bubble' : 'Eliminated'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Record and Stats */}
                        <div className="flex items-center space-x-6">
                          <div className="text-right">
                            <div className="text-lg font-bold text-white">
                              {standing.wins}-{standing.losses}
                              {standing.ties !== undefined && standing.ties > 0 && `-${standing.ties}`}
                            </div>
                            <div className="text-sm text-gray-400">
                              {(standing.win_percentage * 100).toFixed(1)}%
                            </div>
                          </div>

                          {standing.games_behind !== undefined && standing.games_behind > 0 && (
                            <div className="text-right">
                              <div className="text-sm text-gray-500">GB</div>
                              <div className="text-sm font-semibold text-white">
                                {standing.games_behind}
                              </div>
                            </div>
                          )}

                          {standing.streak && (
                            <div className="text-right">
                              <div className="flex items-center space-x-1">
                                <StreakIcon className={`w-4 h-4 ${getStreakColor(standing.streak_type)}`} />
                                <span className={`text-sm font-bold ${getStreakColor(standing.streak_type)}`}>
                                  {standing.streak}
                                </span>
                              </div>
                            </div>
                          )}

                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedTeam === standing.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-border/50 bg-surface/20"
                        >
                          <div className="p-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                              {/* Home Record */}
                              {standing.home_record && (
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                    Home Record
                                  </div>
                                  <div className="text-white font-semibold">
                                    {standing.home_record}
                                  </div>
                                </div>
                              )}

                              {/* Away Record */}
                              {standing.away_record && (
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                    Away Record
                                  </div>
                                  <div className="text-white font-semibold">
                                    {standing.away_record}
                                  </div>
                                </div>
                              )}

                              {/* Division Record */}
                              {standing.division_record && (
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                    Division Record
                                  </div>
                                  <div className="text-white font-semibold">
                                    {standing.division_record}
                                  </div>
                                </div>
                              )}

                              {/* Conference Record */}
                              {standing.conference_record && (
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                    Conference Record
                                  </div>
                                  <div className="text-white font-semibold">
                                    {standing.conference_record}
                                  </div>
                                </div>
                              )}

                              {/* Games Played */}
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                  Games Played
                                </div>
                                <div className="text-white font-semibold">
                                  {standing.games_played}
                                </div>
                              </div>

                              {/* Points (if applicable) */}
                              {standing.points !== undefined && (
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                    Points
                                  </div>
                                  <div className="text-white font-semibold">
                                    {standing.points}
                                  </div>
                                </div>
                              )}

                              {/* Last Updated */}
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                  Last Updated
                                </div>
                                <div className="text-white font-semibold">
                                  {new Date(standing.last_updated).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default StandingsPage;